import { lower } from "case";
import pluralize from "pluralize";
import posthog from "posthog-js";
import React, { ReactElement, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { ListItem, ListItemText, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";

import SaveBackdrop from "./SaveBackdrop";
import { ErrorIcon } from "./icons";
import DeleteButton from "./styled/DeleteButton";

export interface Entry {
  id: string;
  name?: string;
  userId?: string;
}

interface Props {
  name: string;
  open: boolean;
  entries: Entry[];
  onDelete(): Promise<void>;
  onClose(): void;
  buttonText?: string;
}

const DeleteActionDialog = ({
  name,
  open,
  entries,
  onDelete,
  onClose,
  buttonText,
}: Props): ReactElement => {
  const [loading, setLoading] = useState(false);

  const handleClickCancel = (evt: React.MouseEvent): void => {
    evt.preventDefault();
    onClose();
  };

  const handleClickConfirm = (evt: React.MouseEvent): void => {
    evt.preventDefault();
    setLoading(true);
    onDelete().then(
      () => {
        onClose();
        posthog.capture(name + "_deleted", { entries });
        setLoading(false);
      },
      () => {
        onClose();
        setLoading(false);
      },
    );
  };

  const lbl = pluralize(name, entries?.length);
  return (
    <>
      <SaveBackdrop saving={loading} />
      <Dialog
        open={open && !loading}
        onClose={onClose}
        scroll="paper"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          <ErrorIcon color="error" sx={{ mr: 1 }} />
          {buttonText || "Delete"} {lbl}
          <IconButton
            color="primary"
            onClick={onClose}
            size="large"
            sx={{ ml: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack justifyContent="left" alignItems="left">
            <Typography variant="body1">
              {" "}
              {buttonText || "Are you sure you want to delete"} the following{" "}
              {lower(pluralize(name, entries?.length, false))}? This can't be
              undone.
            </Typography>
            <List dense disablePadding>
              {entries?.map((e) => (
                <ListItem key={"deleteDialog" + e?.id}>
                  <ListItemText>
                    {e?.name || e?.userId || e?.id || ""}
                  </ListItemText>
                </ListItem>
              ))}
            </List>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClickCancel}>Cancel</Button>
          <DeleteButton
            variant="contained"
            color="primary"
            onClick={handleClickConfirm}
          >
            {buttonText || "Delete"}
          </DeleteButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default DeleteActionDialog;
